import type { ChatEvent } from "./types";

export type TransportStatus = "connecting" | "live" | "reconnecting" | "offline";

export type TransportOptions = {
  channel: string;
  roomId: string;
  hidden: boolean;
  lastId: () => string | undefined;
  onEvent: (event: ChatEvent) => void;
  onStatus: (status: TransportStatus) => void;
  /* Only the Ably transport can answer this. Polling leaves it unset rather
     than reporting a fabricated count. */
  onPresence?: (count: number) => void;
};

export type ChatConnection = { close: () => void };

const POLL_INTERVAL_MS = 3000;

export const pollingTransport = (options: TransportOptions): ChatConnection => {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const tick = async () => {
    if (stopped) return;
    const after = options.lastId();
    const query = new URLSearchParams();
    if (after) query.set("after", after);

    try {
      const response = await fetch(
        `/api/chat/rooms/${options.roomId}/messages?${query.toString()}`,
        { cache: "no-store" },
      );
      if (!response.ok) throw new Error(String(response.status));
      const payload = await response.json();
      for (const message of payload.data?.messages ?? []) {
        options.onEvent({ type: "message", message });
      }
      if (!stopped) options.onStatus("live");
    } catch {
      if (!stopped) options.onStatus("reconnecting");
    }

    if (!stopped) timer = setTimeout(tick, POLL_INTERVAL_MS);
  };

  options.onStatus("connecting");
  void tick();

  return {
    close: () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    },
  };
};

export const ablyTransport = (options: TransportOptions): ChatConnection => {
  let closed = false;
  let teardown: (() => void) | undefined;

  options.onStatus("connecting");

  void (async () => {
    try {
      /* RealtimePresence is a separate plugin in the modular build — without it
         `channel.presence` is not there at all. */
      const { BaseRealtime, FetchRequest, WebSocketTransport, RealtimePresence } = await import(
        "ably/modular"
      );
      if (closed) return;

      const client = new BaseRealtime({
        authUrl: "/api/chat/token",
        autoConnect: true,
        plugins: { WebSocketTransport, FetchRequest, RealtimePresence },
      });
      const channel = client.channels.get(options.channel);

      void channel
        .subscribe("chat", (message) => {
          options.onEvent(message.data as ChatEvent);
        })
        .catch(() => undefined);

      if (options.onPresence) {
        const report = async () => {
          const members = await channel.presence.get().catch(() => null);
          if (!closed && members) options.onPresence?.(members.length);
        };

        if (options.hidden) void report();
        else void channel.presence.enter().then(report).catch(() => undefined);
        void channel.presence.subscribe(report).catch(() => undefined);
      }

      client.connection.on((change) => {
        if (closed) return;
        if (change.current === "connected") options.onStatus("live");
        else if (change.current === "failed" || change.current === "suspended") {
          options.onStatus("offline");
        } else if (change.current === "connecting" || change.current === "disconnected") {
          options.onStatus("reconnecting");
        }
      });

      teardown = () => {
        channel.unsubscribe();
        client.close();
      };
    } catch {
      if (!closed) options.onStatus("offline");
    }
  })();

  return {
    close: () => {
      closed = true;
      teardown?.();
    },
  };
};
